// Departments: the Ministry's units and the caller's memberships (shell-ia §3.3, §7; SPEC §3.3, C16). The database is the source of truth
// (seeded from spaces/_shared/econic/departments.json), served by `GET /api/econic/departments` and bundled in `GET /api/econic/shell`.
// Only `member && status === "live"` can be switched to; onboarding units open their (empty) Knowledge Bank portal; switching never grants
// data access. WP-SHELL.
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "@ncp/i18n";
import { toast } from "@ncp/ui";
import type { DepartmentUnit, DepartmentsResponse } from "@ncp/assistant-sdk/contracts";
import { useWorkspace } from "../app/workspace-context";
import { markSpaceChosen } from "../os/spaceChoice";
import { useSetPref } from "./prefs";
import { useRefreshShell, useShellSummary } from "./shell/useShellSummary";

export type { DepartmentUnit, DepartmentsResponse };

/** The three live departments by workspace, used only to read a stack that does not serve `/api/econic/departments` yet. */
const LIVE_BY_WORKSPACE: Record<string, { key: string; initials: string }> = {
  "economic-studies": { key: "info-economic-studies", initials: "IES" },
  "economic-policies": { key: "economic-policies-legislation", initials: "EPL" },
  "new-economy": { key: "new-economy", initials: "NE" },
};

export interface DepartmentsState {
  data: DepartmentsResponse | null;
  /** The department in force (the caller's selection, else the first live membership). */
  current: DepartmentUnit | null;
  /** Every live department the caller is a member of, in order. */
  mine: DepartmentUnit[];
  /** Every unit and department, flattened, in order. */
  all: DepartmentUnit[];
  loading: boolean;
  /** "server" = the full model with onboarding units; "workspaces" = the caller's live departments only (older stack). */
  source: "server" | "workspaces";
}

export function flattenUnits(units: DepartmentUnit[]): DepartmentUnit[] {
  const out: DepartmentUnit[] = [];
  for (const u of units) {
    out.push(u);
    if (u.departments?.length) out.push(...u.departments);
  }
  return out;
}

export const canSwitchTo = (u: DepartmentUnit | null | undefined): boolean => !!u && u.member && u.status === "live" && !!u.workspace;

export function useDepartments(): DepartmentsState {
  const shell = useShellSummary();
  const { workspaces, workspaceKey, loading } = useWorkspace();
  const server = shell.data?.departments ?? null;

  if (server) {
    const all = flattenUnits(server.units);
    const mine = all.filter(canSwitchTo);
    // The workspace in force decides (a `?ws=` deep link, a `/w/{ws}/…` link); the server's `current` is the stored choice.
    const current = mine.find((d) => d.workspace === workspaceKey) ?? mine.find((d) => d.key === server.current) ?? mine[0] ?? null;
    return { data: server, current, mine, all, loading: false, source: "server" };
  }

  // Older stack: the caller's own live departments from the catalog's workspaces (real names; no onboarding units are invented).
  const live: DepartmentUnit[] = workspaces
    .filter((w) => LIVE_BY_WORKSPACE[w.key])
    .map((w) => ({
      key: LIVE_BY_WORKSPACE[w.key]!.key,
      kind: "department",
      name: { en: w.name, ar: w.nameAr ?? w.name },
      initials: LIVE_BY_WORKSPACE[w.key]!.initials,
      status: "live",
      workspace: w.key,
      kbPortal: LIVE_BY_WORKSPACE[w.key]!.key,
      member: true,
    }));
  const others = workspaces.filter((w) => !LIVE_BY_WORKSPACE[w.key]).map((w) => ({ key: w.key, name: w.name, nameAr: w.nameAr ?? null, icon: w.icon }));
  const current = live.find((d) => d.workspace === workspaceKey) ?? live[0] ?? null;
  return {
    data: live.length || others.length ? { units: live, current: current?.key ?? null, otherWorkspaces: others } : null,
    current,
    mine: live,
    all: live,
    loading: loading || shell.loading,
    source: "workspaces",
  };
}

/**
 * One workspace and one department (WP-SIDEBAR's request 4): when the workspace in force at sign-in is not a live department of the caller
 * (a workspace remembered from the NCP workspace, e.g. `competition`), the department chip fell back to the first live membership while
 * Today and the snippet resolver kept reading the other workspace. On the first load the shell now selects the department's workspace, so
 * the chip, Today, the resolver and the agent agree. A deep link to another workspace (`?ws=`, `/w/{ws}/…`) is deliberate and kept.
 */
export function useAlignDepartmentWorkspace(): void {
  const { current, mine, loading } = useDepartments();
  const { workspaceKey, setWorkspaceKey, loading: workspacesLoading } = useWorkspace();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || loading || workspacesLoading || !workspaceKey || !mine.length || !current?.workspace) return;
    done.current = true;
    if (params.has("ws") || pathname.startsWith("/w/")) return;
    if (!mine.some((d) => d.workspace === workspaceKey)) setWorkspaceKey(current.workspace);
  }, [loading, workspacesLoading, workspaceKey, mine, current, params, pathname, setWorkspaceKey]);
}

/** A unit's name in the reading language; `short` when asked and present. */
export function unitName(u: Pick<DepartmentUnit, "name" | "short">, locale: string, short = false): string {
  const text = short && u.short ? u.short : u.name;
  return locale === "ar" ? text.ar || text.en : text.en;
}

/**
 * `switchDepartment(key)` (shell-ia §7.3): refuses anything but a live membership; selects the workspace (Today's layer, the sidebar thread,
 * the Tasks filter and the KB default portal follow it), stores `pref:ui.department` for every device and confirms with a toast. On a desk
 * of another department the desk stays open and the toast says where it belongs.
 */
export function useSwitchDepartment(): (key: string) => boolean {
  const { t } = useTranslation();
  const { setWorkspaceKey, workspaceKey, workspaces } = useWorkspace();
  const { all, current } = useDepartments();
  const setPref = useSetPref();
  const refresh = useRefreshShell();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const locale = document.documentElement.lang === "ar" ? "ar" : "en";

  return useCallback((key: string) => {
    const unit = all.find((u) => u.key === key);
    if (!unit || !canSwitchTo(unit)) return false;
    if (unit.key === current?.key && unit.workspace === workspaceKey) return true;
    markSpaceChosen(unit.workspace!);
    setWorkspaceKey(unit.workspace!);
    setPref("ui.department", unit.key);
    refresh();
    const name = unitName(unit, locale);
    const desk = /^\/p\/([^/]+)/.exec(pathname)?.[1];
    const target = workspaces.find((w) => w.key === unit.workspace);
    const deskHere = desk ? !!target?.space?.nav.some((n) => n.page === desk) : true;
    if (desk && !deskHere) {
      const from = workspaces.find((w) => w.key === workspaceKey);
      const deskPage = from?.space?.pages.find((p) => p.key === desk);
      toast(t("assistant:econic.shell.dept.deskElsewhere", {
        desk: deskPage ? (locale === "ar" && deskPage.nameAr ? deskPage.nameAr : deskPage.name) : desk,
        dept: current ? unitName(current, locale) : "",
      }), { action: { label: t("assistant:econic.shell.dept.openWorkspaces"), onClick: () => navigate("/workspaces") } });
    } else {
      toast.success(t("assistant:econic.shell.dept.now", { dept: name }));
    }
    return true;
  }, [all, current, workspaceKey, setWorkspaceKey, setPref, refresh, locale, pathname, workspaces, t, navigate]);
}
